import { DropdownMenuItem, SegmentedToggle } from '@parsleyai/ui'
import { Moon, Sun } from 'lucide-react'
import { useReducedMotion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@/store'

type ThemeValue = 'light' | 'dark'

// Delay before the theme actually flips — long enough to see the thumb slide (the
// SegmentedToggle thumb animates ~0.34s), short enough to still feel responsive. Flipping
// on click would repaint instantly and hide the slide.
const SLIDE_MS = 200

// Keys that switch the theme while the menu row itself holds focus. With two segments,
// ←/→ both flip — the same wrap-around the control has when focused directly.
const FLIP_KEYS = ['ArrowLeft', 'ArrowRight', 'Enter', ' ']

interface Props {
  /** Render as a row of the desktop account menu: ↑/↓ reach it, ←/→ and Enter/Space switch. */
  menuItem?: boolean
}

// The profile-menu light/dark switch, built on the shared `SegmentedToggle` at its
// compact `sm` size. The thumb slides immediately (local `pending` value); the actual theme
// flips once the slide finishes, so you get to see the transition.
export default function ThemeToggle({ menuItem = false }: Props) {
  const { isDark, toggleTheme } = useTheme()
  const { t } = useTranslation()
  const reduced = useReducedMotion()
  const actual: ThemeValue = isDark ? 'dark' : 'light'

  // Displayed value — updated on click so the thumb moves now; kept in sync whenever the
  // real theme changes (our delayed flip, or a change from elsewhere).
  const [pending, setPending] = useState<ThemeValue>(actual)
  useEffect(() => setPending(actual), [actual])

  // Read the live theme inside the delayed callback without a stale closure.
  const isDarkRef = useRef(isDark)
  isDarkRef.current = isDark
  const timer = useRef<number>()
  useEffect(() => () => window.clearTimeout(timer.current), [])

  const handleChange = (next: ThemeValue) => {
    if (next === pending) return
    setPending(next)
    window.clearTimeout(timer.current)
    const flip = () => {
      if (isDarkRef.current !== (next === 'dark')) toggleTheme()
    }
    if (reduced) flip()
    else timer.current = window.setTimeout(flip, SLIDE_MS)
  }

  const toggle = (
    <SegmentedToggle<ThemeValue>
      size="sm"
      className="w-full"
      aria-label={t('nav.userMenu.theme')}
      value={pending}
      onValueChange={handleChange}
      options={[
        { value: 'light', label: t('nav.userMenu.light'), icon: <Sun /> },
        { value: 'dark', label: t('nav.userMenu.dark'), icon: <Moon /> }
      ]}
    />
  )

  if (!menuItem) return toggle

  return (
    <DropdownMenuItem
      // Row highlight on keyboard focus only: a hovered row would frame a control whose
      // segments already carry their own hover state.
      className="rounded-xl px-2 py-1.5 focus:bg-transparent focus:before:opacity-0 focus-visible:bg-accent focus-visible:before:opacity-100"
      onSelect={(e) => e.preventDefault()}
      // Keep focus on the row when a segment is clicked, so ↑/↓ still walk the menu after.
      onMouseDown={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget || !FLIP_KEYS.includes(e.key)) return
        e.preventDefault()
        handleChange(pending === 'dark' ? 'light' : 'dark')
      }}>
      {toggle}
    </DropdownMenuItem>
  )
}
